import { useLayoutEffect, useMemo, useState } from "react";

export type FitToPanelSize = {
  /** Largest slide box of the given aspect ratio that fits the panel page. */
  size: { width: number; height: number };
  /** Height of a single snap page (viewport height minus the reserved top). */
  pageHeight: number;
};

/**
 * Measures the scenes scroll viewport and returns a "contain" fit for a slide
 * of `aspectRatio`: the largest box fitting inside the available page area
 * (viewport minus the sticky toolbar reserve and the horizontal page padding).
 * This yields the redesign behavior — full height when width allows, otherwise
 * width-constrained and centered. Returns zeros until the viewport is measured;
 * callers should fall back to their previous size while that is the case.
 */
export const useFitToPanelSize = (
  scrollEl: HTMLElement | null,
  aspectRatio: number,
  reservedTop: number,
  horizontalPadding: number
): FitToPanelSize => {
  const [viewport, setViewport] = useState({ width: 0, height: 0 });

  useLayoutEffect(() => {
    if (!scrollEl) return;
    const measure = () =>
      setViewport({
        width: scrollEl.clientWidth,
        height: scrollEl.clientHeight,
      });
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(scrollEl);
    return () => observer.disconnect();
  }, [scrollEl]);

  return useMemo(() => {
    const pageHeight = Math.max(0, viewport.height - reservedTop);
    const availableWidth = Math.max(0, viewport.width - horizontalPadding);
    if (pageHeight === 0 || availableWidth === 0 || aspectRatio <= 0) {
      return { size: { width: 0, height: 0 }, pageHeight };
    }
    const width = Math.round(Math.min(availableWidth, pageHeight * aspectRatio));
    const height = Math.round(width / aspectRatio);
    return { size: { width, height }, pageHeight };
  }, [viewport, aspectRatio, reservedTop, horizontalPadding]);
};
